/* eslint-disable import/named */
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query'

import { QUERIES } from '@/enums/queries'
import {
  MFASettingsResponse,
  MFASettingsSetupResponse,
} from '@/types/mfa-settings'

import { invalidateLoadUserDetails } from '../useLoadUsers'
import { disableMFA, setupMFA, verifyMFA } from './services/mutateMFASettings'

type DisableMFAProps = {
  disableMFA: () => Promise<MFASettingsResponse>
} & UseMutationResult<MFASettingsResponse, Error, undefined, unknown>

export const useDisableMFA = (
  options: Omit<
    UseMutationOptions<MFASettingsResponse, Error, undefined, unknown>,
    'mutationKey' | 'mutationFn' | 'onSuccess'
  > = {}
): DisableMFAProps => {
  const mutation = useMutation({
    mutationKey: [QUERIES.USERS.MFA.DISABLE],
    mutationFn: () =>
      disableMFA({
        step: 'disable',
      }),
    onSuccess: async () => {
      await invalidateLoadUserDetails()
    },
    ...options,
  })

  const disableMFAReq = async () => {
    return mutation.mutateAsync(undefined)
  }

  return {
    disableMFA: disableMFAReq,
    ...mutation,
  }
}

type SetupMFAProps = {
  setupMFA: (password: string) => Promise<MFASettingsSetupResponse>
} & UseMutationResult<MFASettingsSetupResponse, Error, string, unknown>

export const useSetupMFA = (
  options: Omit<
    UseMutationOptions<MFASettingsSetupResponse, Error, string, unknown>,
    'mutationKey' | 'mutationFn' | 'onSuccess'
  > = {}
): SetupMFAProps => {
  const mutation = useMutation({
    mutationKey: [QUERIES.USERS.MFA.SETUP],
    mutationFn: (password: string) =>
      setupMFA({
        password,
        step: 'setup',
      }),
    onSuccess: async () => {
      await invalidateLoadUserDetails()
    },
    ...options,
  })

  const setupMFAReq = async (password: string) => {
    return mutation.mutateAsync(password)
  }

  return {
    setupMFA: setupMFAReq,
    ...mutation,
  }
}

type VerifyMFAProps = {
  verifyMFA: (mfa_code: string) => Promise<MFASettingsResponse>
} & UseMutationResult<MFASettingsResponse, Error, string, unknown>

export const useVerifyMFA = (
  options: Omit<
    UseMutationOptions<MFASettingsResponse, Error, string, unknown>,
    'mutationKey' | 'mutationFn' | 'onSuccess'
  > = {}
): VerifyMFAProps => {
  const mutation = useMutation({
    mutationKey: [QUERIES.USERS.MFA.VERIFY],
    mutationFn: (mfa_code: string) => verifyMFA({ mfa_code, step: 'verify' }),
    onSuccess: async () => {
      await invalidateLoadUserDetails()
    },
    ...options,
  })

  const verifyMFAReq = async (mfa_code: string) => {
    return mutation.mutateAsync(mfa_code)
  }

  return {
    verifyMFA: verifyMFAReq,
    ...mutation,
  }
}
